<template>
	<section class="card">
		<header class="card-hdr">
			<div class="title-group">
				<h3>{{ label }}</h3>
				<div
					v-if="currentRange !== 'custom'"
					class="live-status"
				>
					<span
						class="live-indicator"
						:class="{ 'is-delay': isDelay }"
					>
						<span class="dot"></span> {{ isDelay ? "DELAY" : "LIVE" }}
					</span>
					<span
						v-if="lastUpdate"
						class="last-update"
					>
						Ultimo aggiornamento {{ lastUpdate }}
					</span>
				</div>
			</div>

			<div class="range-buttons">
				<button
					@click="fetchData('1h')"
					:class="{ active: currentRange === '1h' }"
					data-range="1h"
				>
					1h
				</button>
				<button
					@click="fetchData('24h')"
					:class="{ active: currentRange === '24h' }"
					data-range="24h"
				>
					24h
				</button>
				<button
					@click="fetchData('7d')"
					:class="{ active: currentRange === '7d' }"
					data-range="7d"
					class="btn-range"
				>
					<span class="full-label">7 giorni</span>
					<span class="short-label">7g</span>
				</button>
				<button
					@click="fetchData('30d')"
					:class="{ active: currentRange === '30d' }"
					data-range="30d"
					class="btn-range"
				>
					<span class="full-label">30 giorni</span>
					<span class="short-label">30g</span>
				</button>
				<button
					@click="openDayPicker"
					:class="{ active: currentRange === 'custom' }"
					data-range="custom"
				>
					{{ dayButtonText }}
				</button>
			</div>
		</header>

		<div
			class="card-body chart-container-rel"
			@wheel="handleWheel"
		>
			<canvas ref="canvas"></canvas>
			<div
				class="zoom-hint-overlay"
				:class="{ show: showZoomHint }"
			>
				<div class="zoom-hint-content">Usa <kbd>Alt</kbd> + rotella per zoomare</div>
			</div>
		</div>

		<DayPickerPopup
			v-if="showDayPicker"
			:isOpen="showDayPicker"
			@close="closeDayPicker"
			@apply="applyDaySelection"
		/>
	</section>
</template>

<script setup>
	import axios from "@/axiosInstance";
import { SENSOR_LABELS } from "@/config/chartConfig";
import { chartTimeConfig, isoToTimestamp } from "@/utils/chartTime";
import Chart from "chart.js/auto";
import "chartjs-adapter-luxon";
import zoomPlugin from "chartjs-plugin-zoom";
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import DayPickerPopup from "../DayPickerPopup.vue";

	Chart.register(zoomPlugin);

	const props = defineProps({
		entity: String,
		deviceId: [String, Number],
		initialRange: { type: String, default: "24h" },
		initialSelectedDay: { type: Object, default: null },
	});

	const emit = defineEmits(["update:range", "update:selectedDay"]);

	const canvas = ref(null);
	const chartInstance = shallowRef(null);

	const showDayPicker = ref(false);
	const showZoomHint = ref(false);
	let hintTimeout = null;

	const currentRange = ref(props.initialRange);
	const selectedDay = ref(props.initialSelectedDay);

	const refreshInterval = ref(null);
	const lastUpdate = ref(null);
	const isDelay = ref(true);

	// =========================
	// UNIT COLORS (UNIFICATI)
	// =========================
	function extractUnitFromLabel(label) {
		const m = String(label).match(/\(([^)]+)\)\s*$/);
		return m ? m[1].trim() : null;
	}

	// Mappa unica, uguale al cross chart
	function unitColor(unitText) {
		if (!unitText) return "rgba(255,255,255,0.85)";
		// unitText può essere "W" o "W / kWh" ecc.
		if (unitText.includes("°C")) return "#22c55e";
		if (unitText.includes("%")) return "#38bdf8";
		if (unitText.includes("V")) return "#f59e0b";
		if (unitText.includes("A")) return "#f97316";
		if (unitText.includes("kWh")) return "#facc15";
		if (unitText.includes("W")) return "#a78bfa";
		return "rgba(255,255,255,0.85)";
	}

	function handleWheel(e) {
		if (!e.altKey) {
			showZoomHint.value = true;
			if (hintTimeout) clearTimeout(hintTimeout);
			hintTimeout = setTimeout(() => (showZoomHint.value = false), 2000);
		} else {
			showZoomHint.value = false;
		}
	}

	const label = computed(() => SENSOR_LABELS[props.entity] || props.entity);

	const dayButtonText = computed(() => {
		if (!selectedDay.value) return "Custom";
		const { start, end } = selectedDay.value;

		if (!(start instanceof Date) || isNaN(start)) return "Custom";

		const from = start.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
		const to = end?.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });

		return to ? `${from} → ${to}` : `${from} → Oggi`;
	});

	// ---------- Polling ----------
	function startPolling() {
		stopPolling();
		refreshInterval.value = setInterval(() => {
			if (currentRange.value !== "custom" || !selectedDay.value?.end) {
				fetchData(currentRange.value, selectedDay.value, true);
			}
		}, 120000);
	}

	function stopPolling() {
		if (refreshInterval.value) {
			clearInterval(refreshInterval.value);
			refreshInterval.value = null;
		}
	}

	// ---------- Fetch ----------
	async function fetchData(range, dayParams = null, isAutoRefresh = false) {
		currentRange.value = range || "24h";
		emit("update:range", currentRange.value);

		const params = {
			entity: props.entity,
			limit: 10000,
		};

		if (range === "custom" && dayParams) {
			params.range = "custom";
			params.start = dayParams.start.toISOString();
			params.end = dayParams.end ? dayParams.end.toISOString() : new Date().toISOString();
		} else {
			params.range = currentRange.value;
		}

		try {
			const response = await axios.get(`/records/devices/${props.deviceId}/measurements`, { params });
			const data = response.data || [];

			const now = new Date();
			lastUpdate.value = now.toLocaleTimeString("it-IT", {
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			});

			// LIVE/DELAY: ultimo punto entro 15 min
			let isLive = false;
			if (data.length > 0) {
				const lastPt = data[data.length - 1];
				if (lastPt?.ts) {
					const lastDate = new Date(lastPt.ts);
					const threshold = 15 * 60 * 1000;
					const diff = now.getTime() - lastDate.getTime();
					isLive = diff <= threshold;
				}
			}
			isDelay.value = !isLive;

			buildChart(data); // (ri)creazione semplice e stabile
		} catch (err) {
			console.error("Errore fetchData ChartBox:", err);
		}
	}

	// ---------- Chart ----------
	function buildChart(data) {
		if (!canvas.value) return;

		if (chartInstance.value) {
			chartInstance.value.destroy();
			chartInstance.value = null;
		}

		const cleaned = data
			.map((d) => ({
				x: isoToTimestamp(d.ts),
				y: typeof d.value_num === "number" ? d.value_num : !isNaN(Number(d.value)) ? Number(d.value) : null,
			}))
			.filter((p) => p.y !== null);

		// unità (presa dal label SENSOR_LABELS)
		const prettyLabel = SENSOR_LABELS[props.entity] || props.entity;
		const unit = extractUnitFromLabel(prettyLabel);
		const unitText = unit ? unit : "";
		const axisTitleColor = unitColor(unitText);

		chartInstance.value = new Chart(canvas.value, {
			type: "line",
			data: {
				datasets: [
					{
						label: props.entity,
						data: cleaned,
						borderWidth: 1,
						tension: 0.1,
						borderColor: "#4caf50",
						backgroundColor: "rgba(76, 175, 80, 0.1)",
						pointRadius: cleaned.length > 100 ? 0 : 2,
						pointHoverRadius: 4,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				animation: false,
				scales: {
					x: {
						...chartTimeConfig.scales.x,
						bounds: "ticks",
						time: {
							...chartTimeConfig.scales.x.time,
							unit: "minute",
							stepSize: currentRange.value === "1h" ? 1 : 25,
							round: "minute",
						},
						ticks: {
							...chartTimeConfig.scales.x.ticks,
							autoSkip: currentRange.value === "1h" || currentRange.value === "24h" ? false : true,
							maxRotation: 45,
							minRotation: 45,
							maxTicksLimit: 100,
							callback: function (value, index, ticks) {
								const date = new Date(value);
								if (currentRange.value !== "1h" && date.getUTCMinutes() % 30 !== 0) return null;
								return chartTimeConfig.scales.x.ticks.callback(value, index, ticks);
							},
						},
						adapters: { ...chartTimeConfig.scales.x.adapters },
					},
					y: {
						beginAtZero: true,
						grid: { color: "rgba(255, 255, 255, 0.1)" },
						ticks: { color: "rgba(255, 255, 255, 0.7)" },
						title: unitText
							? {
									display: true,
									text: unitText,
									color: "rgba(255, 255, 255, 0.7)",
									font: { size: 13, weight: "800" },
									padding: { top: 6, bottom: 6 },
							  }
							: { display: false },
					},
				},
				plugins: {
					zoom: {
						pan: {
							enabled: true,
							mode: "x",
							onPan: ({ chart }) => updateChartIntervals(chart),
						},
						zoom: {
							wheel: { enabled: true, modifierKey: "alt" },
							pinch: { enabled: true },
							mode: "x",
							onZoom: ({ chart }) => updateChartIntervals(chart),
						},
					},
					legend: { display: false },
					tooltip: {
						mode: "index",
						intersect: false,
						callbacks: chartTimeConfig.plugins.tooltip.callbacks,
					},
				},
				interaction: chartTimeConfig.interaction,
			},
		});

		updateChartIntervals(chartInstance.value, currentRange.value);
	}

	// ---------- X intervals ONLY ----------
	function updateChartIntervals(chart, forcedRange = null) {
		if (!chart) return;

		let rangeHours = 24;

		if (forcedRange && forcedRange !== "custom") {
			if (forcedRange === "1h") rangeHours = 1;
			else if (forcedRange === "24h") rangeHours = 24;
			else if (forcedRange === "7d") rangeHours = 168;
			else if (forcedRange === "30d") rangeHours = 720;
		} else {
			const scaleX = chart.scales.x;
			if (scaleX && scaleX.min !== undefined && scaleX.max !== undefined) {
				const rangeMs = scaleX.max - scaleX.min;
				rangeHours = rangeMs / (1000 * 60 * 60);
			}
		}

		let stepSize = 30;
		if (rangeHours <= 1.1) stepSize = 1;
		else if (rangeHours <= 4.1) stepSize = 10;
		else if (rangeHours <= 8.1) stepSize = 15;
		else stepSize = 30;

		if (chart.options.scales.x?.time && chart.options.scales.x?.ticks) {
			chart.options.scales.x.time.unit = "minute";
			chart.options.scales.x.time.stepSize = stepSize;
			chart.options.scales.x.bounds = "ticks";

			if (rangeHours <= 25.1) {
				chart.options.scales.x.ticks.autoSkip = false;
				chart.options.scales.x.ticks.maxRotation = 45;
				chart.options.scales.x.ticks.minRotation = 45;
				chart.options.scales.x.ticks.maxTicksLimit = 100;
			} else {
				chart.options.scales.x.ticks.autoSkip = true;
				chart.options.scales.x.ticks.maxRotation = 0;
				chart.options.scales.x.ticks.minRotation = 0;
			}
		}

		chart.update("none");
	}

	// ---------- Day picker ----------
	function openDayPicker() {
		showDayPicker.value = true;
	}
	function closeDayPicker() {
		showDayPicker.value = false;
	}
	function applyDaySelection(dayData) {
		selectedDay.value = dayData;
		emit("update:selectedDay", dayData);
		fetchData("custom", dayData);
		closeDayPicker();
	}

	// ---------- Watches ----------
	watch(
		() => [props.initialRange, props.initialSelectedDay],
		([newRange, newDay]) => {
			const rangeChanged = newRange !== currentRange.value;
			const dayChanged = JSON.stringify(newDay) !== JSON.stringify(selectedDay.value);

			if (rangeChanged || dayChanged) {
				currentRange.value = newRange;
				selectedDay.value = newDay;
				fetchData(newRange, newDay);
			}
		},
	);

	watch(
		() => [props.entity, props.deviceId],
		() => {
			if (currentRange.value === "custom") {
				currentRange.value = "24h";
				selectedDay.value = null;
				emit("update:range", "24h");
				emit("update:selectedDay", null);
			}
			fetchData(currentRange.value, selectedDay.value);
		},
	);

	// ---------- Lifecycle ----------
	onMounted(() => {
		fetchData(currentRange.value, selectedDay.value);
		startPolling();
	});

	onBeforeUnmount(() => {
		stopPolling();
		if (hintTimeout) clearTimeout(hintTimeout);
		chartInstance.value?.destroy();
	});
</script>

<style scoped>
	.title-group {
		display: flex;
		flex-direction: column; /* Titolo sopra, status sotto */
		align-items: flex-start;
		gap: 4px;
	}

	.live-status {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.live-indicator {
		font-size: 0.7rem;
		font-weight: bold;
		color: #4caf50;
		display: flex;
		align-items: center;
		gap: 5px;
		letter-spacing: 1px;
	}

	.last-update {
		font-size: 0.65rem;
		color: rgba(255, 255, 255, 0.5);
		font-family: monospace;
	}

	.dot {
		width: 7px;
		height: 7px;
		background-color: #4caf50;
		border-radius: 50%;
		display: inline-block;
		animation: pulse 2s infinite;
	}

	@keyframes pulse {
		0% {
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
		}
		70% {
			box-shadow: 0 0 0 8px rgba(76, 175, 80, 0);
		}
		100% {
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
		}
	}

	.live-indicator.is-delay {
		color: #ff9800;
	}

	.live-indicator.is-delay .dot {
		background-color: #ff9800;
		animation: none;
		opacity: 0.8;
	}

	.card {
		border: 1px solid #444;
		border-radius: 8px;
		padding: 16px;
		background: #1a1a1a;
		color: white;
	}

	.card-body {
		height: 360px;
		overflow: hidden;
		position: relative;
	}

	canvas {
		width: 100% !important;
		height: 100% !important;
	}

	.range-buttons {
		display: flex;
		gap: 8px;
		margin-left: auto;
		flex-wrap: wrap;
		justify-content: flex-end;
	}

	.range-buttons button {
		background: #333;
		padding: 4px 10px;
		border: 1px solid #555;
		border-radius: 4px;
		cursor: pointer;
		color: white;
		font-size: 0.85rem;
		white-space: nowrap;
	}

	.short-label {
		display: none;
	}

	@media (max-width: 600px) {
		.card-hdr {
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
		}

		.range-buttons {
			width: 100%;
			justify-content: flex-start;
			overflow-x: auto;
			padding-bottom: 4px; /* Per scrollbar */
			scrollbar-width: none; /* Firefox */
			-ms-overflow-style: none; /* IE/Edge */
			flex-wrap: nowrap;
		}

		.range-buttons::-webkit-scrollbar {
			display: none; /* Chrome/Safari */
		}

		.full-label {
			display: none;
		}

		.short-label {
			display: inline;
		}

		.range-buttons button {
			padding: 6px 12px;
			min-width: fit-content;
		}
	}

	.range-buttons button:hover {
		background: #444;
	}

	.range-buttons button.active[data-range="1h"] {
		background: #e91e63;
		border-color: #e91e63;
	}

	.range-buttons button.active[data-range="24h"] {
		background: #4caf50;
		border-color: #4caf50;
	}

	.range-buttons button.active[data-range="7d"] {
		background: #2196f3;
		border-color: #2196f3;
	}

	.range-buttons button.active[data-range="30d"] {
		background: #ff9800;
		border-color: #ff9800;
	}

	.range-buttons button.active[data-range="custom"] {
		background: #9c27b0;
		border-color: #9c27b0;
	}
</style>
